"use client";

import Image from "next/image";
import LoginButtons from "@/components/LoginButtons";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "../i18n/client";
import { useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";
import { languages, fallbackLng } from "../i18n/settings";

export default function Home() {
  const searchParams = useSearchParams();
  const queryLng = searchParams.get("lng");
  const [lng, setLng] = useState(fallbackLng);

  const { t } = useTranslation(lng, "home");

  useEffect(() => {
    const storedLng = localStorage.getItem("lng");
    const selectedLng = queryLng || storedLng || fallbackLng;
    const finalLng = languages.includes(selectedLng)
      ? selectedLng
      : fallbackLng;
    setLng(finalLng);
    localStorage.setItem("lng", finalLng);
  }, [queryLng]);

  const containerClass = (() => {
    switch (lng) {
      case "th":
        return "font-[family-name:var(--font-noto-sans-thai)]";
      case "ja":
        return "font-[family-name:var(--font-noto-sans-jp)]";
      case "ko":
        return "font-[family-name:var(--font-noto-sans-kr)]";
      case "zh":
        return "font-[family-name:var(--font-noto-sans-sc)]";
      default:
        return "";
    }
  })();

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-between bg-gradient-to-br from-purple-600 to-blue-500 text-white p-4 ${containerClass}`}
    >
      <header className="w-full flex justify-end mb-8">
        <LanguageSwitcher 
          currentLang={lng} 
          onLanguageChange={(newLng) => {
            setLng(newLng);
            localStorage.setItem("lng", newLng);
          }}
        />
      </header>

      <div className="flex-grow flex flex-col items-center justify-center">
        <h1 className="text-5xl font-bold mb-4">{t("welcome")}</h1>
        <p className="text-2xl mb-8">{t("subtitle")}</p>

        <div className="space-y-4 mb-12">
          <LoginButtons />
          <p className="text-center text-sm opacity-80">{t("startChatting")}</p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center">
          <div className="md:w-1/2 mb-8 md:mb-0 md:mr-8">
            <Image
              src="/img/line-chat.png"
              alt="Tofu App Screenshot"
              width={300}
              height={600}
              className="rounded-xl shadow-lg"
            />
          </div>

          <div className="md:w-1/2">
            <h2 className="text-3xl font-semibold mb-4">
              {t("features.title")}
            </h2>
            <ul className="space-y-2 text-lg">
              {t("features.list", { returnObjects: true }).map(
                (feature, index) => (
                  <li key={index}>• {feature}</li>
                )
              )}
            </ul>
          </div>
        </div>
      </div>

      <footer className="w-full mt-12 py-4 border-t border-white/20">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm mb-2 md:mb-0">{t("footer.copyright")}</p>
          <div className="flex space-x-4">
            <a
              href="mailto:tofu@tmatter.co"
              className="text-sm hover:underline"
            >
              {t("footer.email")}
            </a>
            <a
              href="https://fb.me/tofukoro"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:underline"
            >
              {t("footer.facebook")}
            </a>
            <a
              href="https://x.com/tofukoro"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:underline"
            >
              {t("footer.twitter")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
